import { useState } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { sendMessage } from '@/features/chat/chatSlice';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';

interface DynamicTableProps {
  columns: string[] | Array<{header: string, accessor: string}>;
  rows: (string | number)[] | Record<string, any>[];
  itemType: string; // e.g., 'paper type', 'printer'
}

export const DynamicTable = ({ columns, rows, itemType }: DynamicTableProps) => {
  const dispatch = useAppDispatch();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Validate that rows is an array
  if (!Array.isArray(rows)) {
    console.error('DynamicTable: rows is not an array:', rows);
    return (
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        <p className="text-red-500">Error: Invalid data format for table</p>
        <pre className="text-xs mt-2">{JSON.stringify({ columns, rows, itemType }, null, 2)}</pre>
      </div>
    );
  }

  // Validate that columns is an array
  if (!Array.isArray(columns)) {
    console.error('DynamicTable: columns is not an array:', columns);
    return (
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        <p className="text-red-500">Error: Invalid columns format for table</p>
        <pre className="text-xs mt-2">{JSON.stringify({ columns, rows, itemType }, null, 2)}</pre>
      </div>
    );
  }

  // Extract column names and accessors
  const columnNames = columns.map(col => typeof col === 'string' ? col : col.header);
  const columnAccessors = columns.map(col => typeof col === 'string' ? col : col.accessor);

  console.log('Column names:', columnNames);
  console.log('Column accessors:', columnAccessors);

  // Convert object rows to array format for display
  const processedRows = rows.map((row, index) => {
    if (Array.isArray(row)) {
      // Row is already an array, use as-is
      return row;
    } else if (typeof row === 'object' && row !== null) {
      // Row is an object, extract values based on column accessors
      return columnAccessors.map(accessor => {
        const value = row[accessor];
        return value !== undefined ? String(value) : '';
      });
    } else {
      console.error(`DynamicTable: row ${index} is neither array nor object:`, row);
      return [];
    }
  }).filter(row => row.length > 0); // Remove empty rows

  if (processedRows.length === 0) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        <p className="text-red-500">Error: No valid rows found after processing</p>
        <pre className="text-xs mt-2">{JSON.stringify({ columns, rows, itemType }, null, 2)}</pre>
      </div>
    );
  }

  const handleRowClick = (row: (string | number)[], index: number) => {
    setSelectedIndex(index);
    // Assuming the first column is the primary identifier/name.
    const selection = row[0];
    const message = `I'll select option ${index + 1}, the ${selection} ${itemType}.`;
                        dispatch(sendMessage({ userFacingMessage: message, internalMessage: message }));
  };

  // Capitalize the first letter of the itemType for the title
  const title = `Please Select a ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`;

  return (
    <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12 text-center text-sm font-medium text-gray-500 dark:text-gray-400">#</TableHead>
              {columnNames.map((columnName, index) => (
                <TableHead key={index} className="text-sm font-medium text-gray-500 dark:text-gray-400">{columnName}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {processedRows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                onClick={() => handleRowClick(row, rowIndex)}
                className={`cursor-pointer transition-colors duration-200 ease-in-out even:bg-gray-50/50 dark:even:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  selectedIndex === rowIndex ? 'bg-blue-100 dark:bg-blue-900/50' : ''
                }`}
              >
                <TableCell className="text-center font-medium text-gray-500 dark:text-gray-400">{rowIndex + 1}</TableCell>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex} className="py-3 px-4 text-gray-700 dark:text-gray-200">{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
